import {
  parseServerDatabaseRootDocId,
  parseServerDatabaseRowShardDocId,
} from '@streamlain/database-identity';
import { type Array as YArray, Doc as YDoc, type Map as YMap } from 'yjs';

import { safeApplyUpdate } from './safe-apply-update';

type PromiseResult<T> = T extends Promise<infer R> ? R : never;
export type IdConverter = PromiseResult<ReturnType<typeof getIdConverter>>;

export async function getIdConverter(
  storage: {
    getDocBuffer: (id: string) => Promise<Uint8Array | null>;
  },
  spaceId: string
) {
  const oldIdToNewId: Record<string, string> = { [spaceId]: spaceId };
  const newIdToOldId: Record<string, string> = { [spaceId]: spaceId };

  const rootDocBuffer = await storage.getDocBuffer(spaceId);
  if (rootDocBuffer) {
    const ydoc = new YDoc({
      guid: spaceId,
    });

    // Critical: If root doc is corrupt, ID mappings will be wrong
    // This can cause data to be saved under wrong docIds
    const result = safeApplyUpdate(ydoc, rootDocBuffer, {
      context: `id-converter:rootDoc:${spaceId}`,
      validate: true, // Critical: corrupt root doc causes wrong ID mappings
    });
    if (!result.success) {
      console.error(
        `[IdConverter] CRITICAL: Failed to apply root doc for space ${spaceId}: ${result.error.message}. ` +
          `ID mappings will be incomplete - this may cause data integrity issues!`
      );
      // Return identity mappings - better than crashing, but may cause issues
      return {
        newIdToOldId: (newId: string) => newId,
        oldIdToNewId: (oldId: string) => oldId,
      };
    }

    // get all ids from rootDoc.meta.pages.[*].id, trust this id as normalized id
    const normalizedDocIds = (
      (ydoc.getMap('meta') as YMap<any> | undefined)?.get('pages') as
        | YArray<YMap<any>>
        | undefined
    )
      ?.map(i => i.get('id') as string)
      .filter(i => !!i);

    const spaces = ydoc.getMap('spaces') as YMap<any> | undefined;
    for (const pageId of normalizedDocIds ?? []) {
      const subdoc = spaces?.get(pageId);
      if (subdoc && subdoc instanceof YDoc) {
        oldIdToNewId[subdoc.guid] = pageId;
        newIdToOldId[pageId] = subdoc.guid;
      }
    }
  }

  return {
    newIdToOldId(newId: string) {
      if (parseServerDatabaseRowShardDocId(newId, spaceId)) {
        return newId;
      }
      if (newId.startsWith(`db$${spaceId}$`)) {
        return newId;
      }
      if (newId.startsWith(`db$`)) {
        // db$docId -> db$${spaceId}$docId
        return newId.replace(`db$`, `db$${spaceId}$`);
      }
      if (newId.startsWith(`userdata$`)) {
        // userdata$userId$docId -> userdata$userId$spaceId$docId
        return newId.replace(
          new RegExp(`^(userdata\\$[\\w-]+)\\$([^\\$]+)`),
          (_, p1, p2) => `${p1}$${spaceId}$${p2}`
        );
      }
      return newIdToOldId[newId] ?? newId;
    },
    oldIdToNewId(oldId: string) {
      if (parseServerDatabaseRowShardDocId(oldId, spaceId)) {
        return oldId;
      }
      if (parseServerDatabaseRootDocId(oldId, spaceId)) {
        return oldId;
      }
      // db$${spaceId}$docId -> db$docId
      if (oldId.startsWith(`db$${spaceId}$`)) {
        return oldId.replace(`db$${spaceId}$`, `db$`);
      }
      // userdata$userId$spaceId$docId -> userdata$userId$docId
      if (oldId.match(new RegExp(`^userdata\\$[\\w-]+\\$${spaceId}\\$`))) {
        return oldId.replace(`$${spaceId}$`, '$');
      }
      return oldIdToNewId[oldId] ?? oldId;
    },
  };
}
